import {
  Box,
  Col,
  FormItem,
  Icon,
  Input,
  Row,
  SelectDates,
  Text,
  TextArea,
} from '@cubeyou-team/hx-react-components';
import React, { FC } from 'react';
import {
  Controller,
  Control,
  UseFormTrigger,
  UseFormGetValues,
} from 'react-hook-form';
import styled from 'styled-components';

import { MODAL_TYPES } from 'src/components/RepositoryItemActions/constants';
import translations from 'src/lang';
import { useAudienceActivationContext } from 'src/modules/audience-activation/providers/AudienceActivationProvider';
import { DestinationsContainer } from '../DestinationsContainer';
import { RequiredText } from '../RequiredText';
import { FormHeader } from './FormHeader';

const lang = 'en-US';

const StyledAudienceActivationModalForm = styled.div`
  padding-bottom: 0;

  .paragraph {
    overflow: hidden;
    width: 450px;
    padding: 0 0.5rem;
  }

  div[type='datePicker'] > div:last-child {
    max-width: 300px;
  }
`;

interface AudienceActivationModalFormProps {
  control: Control<any>;
  errors: any;
  trigger: UseFormTrigger<any>;
  getValues: UseFormGetValues<any>;
}

export const AudienceActivationModalForm: FC<AudienceActivationModalFormProps> = ({
  control,
  errors,
  trigger,
  getValues,
}) => {
  const { modalType } = useAudienceActivationContext();

  const {
    dateRange: { startDate, endDate },
  } = getValues();

  return (
    <>
      <StyledAudienceActivationModalForm>
        <Box pB="medium">
          <Col span="auto">
            <Box pB="large">
              <Box pB="small">
                <Row valign="middle" gutter="small">
                  <Col span="auto">
                    <Icon size="medium" icon="icon-social" />
                  </Col>
                  <Text variant="dark" size="xbody" weight="semi-bold">
                    {
                      translations[lang].MODALS.AUDIENCE_ACTIVATION
                        .CONTENT_TITLE
                    }
                  </Text>
                </Row>
              </Box>
              <FormHeader modalType={modalType} />
            </Box>

            <Controller
              name="campaignName"
              control={control}
              render={({ field }) => (
                <FormItem
                  error={!!errors.campaignName}
                  message={errors.campaignName?.message}
                  label={
                    translations[lang].MODALS.AUDIENCE_ACTIVATION
                      .CAMPAIGN_NAME_INPUT_LABEL
                  }
                >
                  <Input
                    {...field}
                    placeholder={
                      translations[lang].MODALS.AUDIENCE_ACTIVATION
                        .CAMPAIGN_NAME_INPUT_LABEL
                    }
                    type="text"
                    size="small"
                    tabIndex={1}
                    disabled={modalType === MODAL_TYPES.OPEN_CAMPAIGN}
                    required
                    error={!!errors.campaignName}
                  />
                </FormItem>
              )}
            />

            <Controller
              name="dateRange"
              control={control}
              render={({ field }) => (
                <FormItem
                  error={
                    !!errors.dateRange?.startDate || !!errors.dateRange?.endDate
                  }
                  message={
                    errors.dateRange?.startDate?.message ||
                    errors.dateRange?.endDate?.message
                  }
                  label={
                    translations[lang].MODALS.AUDIENCE_ACTIVATION
                      .CAMPAIGN_DATE_PICKER_LABEL
                  }
                >
                  <SelectDates
                    dropdownPosition="left"
                    onSaveDates={(dates: string[]) => {
                      field.onChange({
                        startDate: dates[0] || '',
                        endDate: dates[1] || '',
                      });
                      trigger('dateRange');
                    }}
                    calendarChoice="range"
                    minYear={2020}
                    maxYear={new Date().getFullYear()}
                    blockDates="past"
                    placeholder={
                      translations[lang].MODALS.AUDIENCE_ACTIVATION
                        .CAMPAIGN_DATE_PICKER_PLACEHOLDER
                    }
                    selectedStartDate={startDate || ''}
                    selectedEndDate={endDate || ''}
                    disabled={modalType === MODAL_TYPES.OPEN_CAMPAIGN}
                    format={'YYYY-MM-DD'}
                    size="small"
                    setLargeMinWidth={false}
                    onBlur={() => trigger('dateRange')}
                    error={
                      !!errors.dateRange?.startDate ||
                      !!errors.dateRange?.endDate
                    }
                  />
                </FormItem>
              )}
            />
            <DestinationsContainer />
            {modalType !== MODAL_TYPES.OPEN_CAMPAIGN && (
              <Box pT="large">
                <Controller
                  name="additionalNotes"
                  control={control}
                  render={({ field }) => (
                    <FormItem
                      error={!!errors.additionalNotes}
                      message={errors.additionalNotes?.message}
                      label={
                        translations[lang].MODALS.AUDIENCE_ACTIVATION
                          .ADDITIONAL_NOTES_INPUT_LABEL
                      }
                    >
                      <TextArea
                        {...field}
                        placeholder={
                          translations[lang].MODALS.AUDIENCE_ACTIVATION
                            .ADDITIONAL_NOTES_PLACEHOLDER
                        }
                        type="text"
                        size="small"
                        tabIndex={1}
                        error={!!errors.additionalNotes}
                      />
                    </FormItem>
                  )}
                />
              </Box>
            )}
          </Col>
        </Box>
      </StyledAudienceActivationModalForm>
      <RequiredText />
    </>
  );
};
